import React from "react";
import { Link } from "react-router-dom";

// @ts-ignore
import LogoWithType from "svg/logo-with-type.svg";
import CallOrSchedule, {
  CallOrScheduleDetails,
} from "components/CallOrSchedule";
import UserNav from "./UserNav";
import { QuestionMarkCircleIcon } from "@heroicons/react/24/outline";

type HeaderProps = {
  properties?: {
    call_or_schedule?: CallOrScheduleDetails;
  };
  logoLinksHome?: boolean;
  basecamp?: boolean;
  /** When set, shows the help affordance linking to the knowledge base. */
  helpHref?: string;
  /** The pre-need quote flow's bar: logo pinned left with the page's
   * `actions` on the right, and no account menu — the page is reached by
   * link, and account chrome would only distract from the flow. */
  quoteFlow?: boolean;
  /** The right-hand slot of the quoteFlow bar. */
  actions?: React.ReactNode;
  /** The quoteFlow bar's content width, matched to the page below it so
   * the logo and actions sit flush with the content edges — "form" over
   * the application's column, "wide" over card-grid pages. */
  quoteFlowWidth?: QuoteFlowWidth;
};

export type QuoteFlowWidth = "form" | "wide";

const QUOTE_FLOW_WIDTH_CLASS: Record<QuoteFlowWidth, string> = {
  form: "max-w-[760px]",
  wide: "max-w-[1180px]",
};

const Header: React.FC<HeaderProps> = ({
  properties,
  logoLinksHome = false,
  basecamp = false,
  helpHref,
  quoteFlow = false,
  actions,
  quoteFlowWidth = "wide",
}) => {
  // The mockup's wordmark height (22px at the logo's 667:164 aspect).
  const Logo = (
    <div className="w-[90px]">
      <LogoWithType />
    </div>
  );

  if (quoteFlow) {
    return (
      <header className="items-center justify-center">
        <div
          className={`mx-auto flex h-18 w-full items-center justify-between gap-4 px-4 2xs:px-5 sm:px-10 ${QUOTE_FLOW_WIDTH_CLASS[quoteFlowWidth]}`}
        >
          <div className="flex flex-none items-center justify-start">
            {Logo}
          </div>
          <div className="flex min-w-0 justify-end">{actions}</div>
        </div>
      </header>
    );
  }

  return (
    <header
      className={`items-center justify-center ${basecamp ? "border-b border-oat bg-white" : ""}`}
    >
      <div className="mx-auto flex h-18 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        {basecamp ? (
          <>
            <div className="flex items-center justify-start gap-2">
              <UserNav />
              <Link to="/">{Logo}</Link>
            </div>

            <div className="flex justify-end">
              {helpHref && (
                <Link
                  to={helpHref}
                  className="flex items-center gap-2 rounded-md px-3 py-2 transition-colors hover:bg-oat focus:outline-none focus:ring-2 focus:ring-cantelope focus:ring-opacity-20"
                >
                  <QuestionMarkCircleIcon
                    className="h-4 w-4 text-plum"
                    aria-hidden="true"
                  />
                  <span className="text-sm font-bold text-plum">
                    Need Help?
                  </span>
                </Link>
              )}
            </div>
          </>
        ) : properties?.call_or_schedule ? (
          <>
            <div className="flex items-center justify-start gap-2">
              <UserNav />
              {logoLinksHome ? <Link to="/">{Logo}</Link> : Logo}
            </div>

            <div className="flex justify-end">
              <CallOrSchedule details={properties.call_or_schedule} />
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-1 justify-start">
              <UserNav />
            </div>

            <div className="flex justify-center">
              {logoLinksHome ? <Link to="/">{Logo}</Link> : Logo}
            </div>

            <div className="flex flex-1 justify-end" />
          </>
        )}
      </div>
    </header>
  );
};

export default Header;
